import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
  Toast,
  ToastHeader,
  ToastBody,
} from "reactstrap";
import { TitleBar } from "../../Components/Common/TitleBar/titleBar";
import avatar from "../../assets/images/users/user-dummy-img.jpg";
import { TripModal } from "../../Components/Common/TripModal/tripModal";
import { useDispatch, useSelector } from "react-redux";
import {
  changePassword,
  getUserDetails,
  updateProfileDetails,
  updateProfilePic,
} from "../../slices/thunks";
import { Formik } from "formik";
import * as Yup from "yup";
import { hideErrorMessage, setLoading } from "../../slices/auth/profile/reducer";
import { useNavigate } from "react-router-dom";
import { removeCountryCode } from "../../constant/utils";
import ResponsiveTabs from "../../Components/Common/ResponsiveTabs";
import PhoneNumberField from "../../Components/Common/PhoneNumberField";

const tabs = [
  {
    id: "1",
    label: "Basic Details",
    path: "",
  },
  { id: "2", label: "Change Password", path: "" },
];

const UserProfile = () => {
  const dispatch = useDispatch();

  const formikRef = useRef(null);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("1");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toasterType, setToasterType] = useState("");

  const userDetails = useSelector((state) => state.Profile.userDetails);
  const errorMessage = useSelector(
    (state) => state.Profile.profileErrorMessage
  );
  const loading = useSelector((state) => state.Profile.loading);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profilePic, setProfilePic] = useState(avatar);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };

  const profileSchema = Yup.object().shape({
    first_name: Yup.string()
      .trim()
      .min(1, "First Name is required")
      .required("First Name is required"),
    last_name: Yup.string()
      .trim()
      .min(1, "Last Name is required")
      .required("Last Name is required"),
    phone_number: Yup.string()
      .trim()
      .required("Phone number is required"),
  });
  

  const passwordSchema = Yup.object().shape({
    old_password: Yup.string().required("Old password is required"),
    new_password: Yup.string()
      .required("New password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/\d/, "Password must contain at least one number")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain one special character"
      ),
    confirm_password: Yup.string()
      .required("Confirm password is required")
      .oneOf([Yup.ref("new_password"), null], "Passwords must match"),
  });

  const handleProfilePicChange = async (event) => {
    try {
      const file = event.target.files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

      if (!file) {
        showToaster(false, "Please select a file");
        return;
      }

      if (!validTypes.includes(file.type)) {
        showToaster(false, "Please select a valid image file");
        return;
      }

      if (file) {
        const imageUrl = URL.createObjectURL(file);
        setProfilePic(imageUrl);
        const formData = new FormData();
        formData.append("profile_picture", file);
        const response = await dispatch(updateProfilePic(formData));

        const { success: isSuccess, message } = response;
        showToaster(isSuccess, message);

      }
    } catch (error) {
      console.log("error", error);
      showToaster(false, error.message || "An unexpected error occurred");
    } finally {
      dispatch(getUserDetails());
    }
  };

  const handleProfileSubmit = async (values) => {
    try {
      const response = await dispatch(updateProfileDetails(values));
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
    } catch (error) {
      console.log("error", error);
      showToaster(false, error.message || "An unexpected error occurred");
    } finally {
      dispatch(getUserDetails());
      toggleModal();
    }
  };

  const handlePasswordSubmit = async (values) => {
    try {
      const response = await dispatch(changePassword(values));
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
      setTimeout(() => {
        navigate("/logout");
      }, 3000);

    } catch (error) {
      console.log("error", error);
      showToaster(false, error.message || "An unexpected error occurred");
    } finally {
      dispatch(getUserDetails());
    }
  };

  useEffect(() => {
    setProfilePic(
      userDetails.profile_picture_url ? userDetails.profile_picture_url : avatar
    );
  }, [userDetails]);

  document.title = "Profile | D&A - Admin";


  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <TitleBar title={"Profile"} showIcon={true} />
          <div
            className="position-fixed top-0 end-0 p-3"
            style={{ zIndex: 1055 }}
          >
            <Toast isOpen={showToast}>
              <ToastHeader
                icon={toasterType === "danger" ? "danger" : "success"}
                toggle={() => setShowToast(false)}
              >
                {toasterType === "danger" ? "Error" : "Success"}
              </ToastHeader>
              <ToastBody>{toastMessage}</ToastBody>
            </Toast>
          </div>

          <div className="profileSection">
            <div>
              <Row>
                <ResponsiveTabs
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  tabs={tabs}
                  isResponsive={false}
                />
                <Col md={12}>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <Row className="mt-4 mt-md-5">
                        <Col xl={2} className="p-2 p-md-0 text-center profile border-bottom border-sm-0">
                          <div className="position-relative changeProfile">
                            {loading.profilePicUpdate ? (
                              <div
                                className="d-flex justify-content-center align-items-center"
                                style={{ height: "120px" }}
                              >
                                <Spinner color="primary" />
                              </div>
                            ) : (
                              <img
                                src={profilePic}
                                alt="Profile"
                                className="profileImage rounded-circle img-thumbnail"
                              />
                            )}

                            <input
                              type="file"
                              accept="image/* "
                              className="d-none"
                              id="profilePicInput"
                              onChange={handleProfilePicChange}
                            />
                            <div
                              className="camera-icon position-absolute top-100 start-50 translate-middle bg-white p-1 rounded-circle border"
                              onClick={() =>
                                document
                                  .getElementById("profilePicInput")
                                  .click()
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <i className="ri-camera-fill"></i>
                            </div>
                          </div>
                          <h5 className="mt-3 mb-2 userName">
                            {userDetails.first_name} {userDetails.last_name}
                          </h5>
                          <p className="userRole">Admin</p>

                          <Button
                            className="editProfileButton"
                            onClick={toggleModal}
                          >
                            <i className="ri-edit-box-line"></i> Edit Profile
                          </Button>
                        </Col>
                        <Col md={1} className="saperatorWrap">
                          <div className="detailsSaperator" />
                        </Col>
                        <Col
                          md={12}
                          xl={4}
                          className="d-flex flex-column mt-3"
                        >
                          <Col className="d-flex flex-xl-column gap-3 align-items-center align-items-xl-start gap-xl-0">
                            <p className="profileFieldName">First Name<span className="d-inline d-xl-none">:</span></p>
                            <p className="profileValue">
                              {userDetails?.first_name}
                            </p>
                          </Col>
                          <Col className="d-flex flex-xl-column mt-3 gap-3 align-items-center align-items-xl-start gap-xl-0">
                            <p className="profileFieldName">Phone Number<span className="d-inline d-xl-none">:</span></p>
                            <p className="profileValue">
                              {removeCountryCode(
                                userDetails?.country_code,
                                userDetails?.phone_number
                              )}
                            </p>
                          </Col>
                          <Col className="d-flex flex-xl-column mt-3 gap-3 align-items-center align-items-xl-start gap-xl-0">
                            <p className="profileFieldName">Email<span className="d-inline d-xl-none">:</span></p>
                            <p className="profileValue">
                              {userDetails?.email}
                            </p>
                          </Col>
                        </Col>

                        <Col
                          md={12}
                          xl={4}
                          className="d-flex flex-column"
                        >
                          <Col className="d-flex flex-xl-column mt-3 gap-3 align-items-center align-items-xl-start gap-xl-0">
                            <p className="profileFieldName">Last Name<span className="d-inline d-xl-none">:</span></p>
                            <p className="profileValue">
                              {userDetails?.last_name}
                            </p>
                          </Col>
                          <Col className="d-flex flex-xl-column mt-3 gap-3 align-items-center align-items-xl-start gap-xl-0">
                            <p className="profileFieldName">Role<span className="d-inline d-xl-none">:</span></p>
                            <p className="profileValue">Admin</p>
                          </Col>
                          <Col className="d-none d-xl-flex flex-column mt-3"></Col>
                        </Col>
                      </Row>
                    </TabPane>

                    <TabPane tabId="2">
                      <div className="mt-4">
                        <Formik
                          initialValues={{
                            old_password: "",
                            new_password: "",
                            confirm_password: "",
                          }}
                          validationSchema={passwordSchema}
                          onSubmit={handlePasswordSubmit}
                        >
                          {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                          }) => (
                            <Form onSubmit={handleSubmit}>
                              <Row>
                                <Col xl={4}>
                                  <FormGroup>
                                    <Label
                                      for="old_password"
                                      className="passwordLabel"
                                    >
                                      Old Password
                                    </Label>
                                    <Input
                                      type="password"
                                      name="old_password"
                                      className="profileInput"
                                      id="old_password"
                                      placeholder="Enter Old Password"
                                      value={values.old_password}
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      invalid={
                                        touched.old_password &&
                                          errors.old_password
                                          ? true
                                          : false
                                      }
                                    />
                                    {touched.old_password &&
                                      errors.old_password ? (
                                      <FormFeedback type="invalid">
                                        {errors.old_password}
                                      </FormFeedback>
                                    ) : null}
                                  </FormGroup>
                                </Col>
                                <Col xl={4}>
                                  <FormGroup>
                                    <Label
                                      for="new_password"
                                      className="passwordLabel"
                                    >
                                      New Password
                                    </Label>
                                    <Input
                                      type="password"
                                      name="new_password"
                                      id="new_password"
                                      className="profileInput"
                                      placeholder="Enter New Password"
                                      value={values.new_password}
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      invalid={
                                        touched.new_password &&
                                          errors.new_password
                                          ? true
                                          : false
                                      }
                                    />
                                    {touched.new_password &&
                                      errors.new_password ? (
                                      <FormFeedback type="invalid">
                                        {errors.new_password}
                                      </FormFeedback>
                                    ) : null}
                                  </FormGroup>
                                </Col>
                                <Col xl={4}>
                                  <FormGroup>
                                    <Label
                                      for="confirm_password"
                                      className="passwordLabel"
                                    >
                                      Confirm New Password
                                    </Label>
                                    <Input
                                      type="password"
                                      name="confirm_password"
                                      className="profileInput"
                                      id="confirm_password"
                                      placeholder="Re-enter New Password"
                                      value={values.confirm_password}
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      invalid={
                                        touched.confirm_password &&
                                          errors.confirm_password
                                          ? true
                                          : false
                                      }
                                    />
                                    {touched.confirm_password &&
                                      errors.confirm_password ? (
                                      <FormFeedback type="invalid">
                                        {errors.confirm_password}
                                      </FormFeedback>
                                    ) : null}
                                  </FormGroup>
                                </Col>
                              </Row>

                              <div className="d-flex justify-content-end passwordButtonWrap">
                                <Button
                                  onClick={() => setActiveTab("1")}
                                  className="close-button"
                                >
                                  Close without saving
                                </Button>
                                <Button
                                  color="primary"
                                  type="submit"
                                  className="save-button"
                                >
                                  {loading.passwordChange ? <Spinner /> : "Save"}
                                </Button>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      </div>
                    </TabPane>
                  </TabContent>
                </Col>
              </Row>
            </div>
          </div>
        </Container>

        <TripModal
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          buttonText="Update"
          closeText={"Close without saving"}
          title={"Profile"}
          tripText={"Edit Profile"}
          loading={loading.update}
          onSubmit={() => {
            if (formikRef.current) {
              formikRef.current.handleSubmit();
            }
          }}
        >
          <div className="px-3 d-flex flex-column gap-4">
            <Formik
              innerRef={formikRef}
              initialValues={{
                first_name: userDetails.first_name || "",
                last_name: userDetails.last_name || "",
                email: userDetails.email || "",
                phone_number: userDetails.phone_number || "",
                country_code: userDetails.country_code || "us",
              }}
              validationSchema={profileSchema}
              onSubmit={handleProfileSubmit}

            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="first_name" className="passwordLabel">
                          First Name
                        </Label>
                        <Input
                          id="first_name"
                          type="text"
                          name="first_name"
                          className="profileInput"
                          placeholder="Enter First Name"
                          value={values.first_name}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          invalid={
                            touched.first_name && errors.first_name
                              ? true
                              : false
                          }
                        />
                        {touched.first_name && errors.first_name ? (
                          <FormFeedback type="invalid">
                            {errors.first_name}
                          </FormFeedback>
                        ) : null}
                      </FormGroup>

                      <FormGroup>
                        <Label for="email" className="passwordLabel">
                          Email
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          name="email"
                          disabled
                          className="profileInput"
                          placeholder="Enter email"
                          value={values.email}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          invalid={touched.email && errors.email ? true : false}
                        />
                        {touched.email && errors.email ? (
                          <FormFeedback type="invalid">
                            {errors.email}
                          </FormFeedback>
                        ) : null}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="last_name" className="passwordLabel">
                          Last Name
                        </Label>
                        <Input
                          id="last_name"
                          type="text"
                          name="last_name"
                          className="profileInput"
                          placeholder="Enter Last Name"
                          value={values.last_name}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          invalid={
                            touched.last_name && errors.last_name ? true : false
                          }
                        />
                        {touched.last_name && errors.last_name ? (
                          <FormFeedback type="invalid">
                            {errors.last_name}
                          </FormFeedback>
                        ) : null}
                      </FormGroup>
                      <FormGroup>

                        <PhoneNumberField
                          label="Phone Number"
                          phoneValue={values.phone_number}
                          countryCode={userDetails.country_code}
                          onChange={(phone, code) => {
                            formikRef.current.setFieldValue(
                              "phone_number",
                              phone
                            );
                            formikRef.current.setFieldValue("country_code", code);
                          }}
                          onBlur={handleBlur}
                        />
                        {touched.phone_number && errors.phone_number && (
                          <FormFeedback className="d-block">
                            {errors.phone_number}
                          </FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </div>
        </TripModal>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
